import React, { useState } from 'react';
import SEOScore from './SEOScore';
import './NestedRedirects.css';

const NestedRedirects = ({ nestedRedirects, stats }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyCycles, setShowOnlyCycles] = useState(false);

  const filteredRedirects = nestedRedirects.filter(redirect => {
    const matchesSearch = !searchTerm || 
      redirect.chain.some(step => 
        step.source.toLowerCase().includes(searchTerm.toLowerCase()) ||
        step.target.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesCycleFilter = !showOnlyCycles || redirect.hasCycle;
    
    return matchesSearch && matchesCycleFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case '301': return '#28a745';
      case '302': return '#ffc107';
      case '404': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div className="nested-redirects">
      <div className="nested-controls">
        <div className="nested-stats">
          <div className="stat-grid">
            <div className="stat-item">
              <span className="stat-value">{stats.totalChains}</span>
              <span className="stat-label">Total Chains</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.totalCycles}</span>
              <span className="stat-label">Cycles</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.longestChain}</span>
              <span className="stat-label">Longest Chain</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.averageChainLength}</span>
              <span className="stat-label">Avg Length</span>
            </div>
          </div>
        </div>
        
        <div className="nested-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search redirects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <label className="cycle-filter">
            <input
              type="checkbox"
              checked={showOnlyCycles}
              onChange={(e) => setShowOnlyCycles(e.target.checked)}
            />
            <span>Show only cycles</span>
          </label>
        </div>
      </div>

      <div className="redirects-container">
        {filteredRedirects.length === 0 ? (
          <div className="no-results">
            {searchTerm || showOnlyCycles 
              ? 'No redirects match your filters' 
              : 'No nested redirects found'
            }
          </div>
        ) : (
          <div className="redirect-chains">
            {filteredRedirects.map((redirectChain) => (
              <div 
                key={redirectChain.id} 
                className={`redirect-chain ${redirectChain.hasCycle ? 'has-cycle' : ''}`}
              >
                <div className="chain-header">
                  <div className="chain-info">
                    <span className="chain-length">{redirectChain.length} steps</span>
                    {redirectChain.seoAnalysis && (
                      <SEOScore 
                        score={redirectChain.seoAnalysis.chainScore}
                        grade={redirectChain.seoAnalysis.grade}
                        compact={true}
                      />
                    )}
                    {redirectChain.hasCycle && (
                      <span className="cycle-badge">🔄 Cycle Detected</span>
                    )}
                  </div>
                  <div className="final-target">
                    <strong>Final Target:</strong> {redirectChain.finalTarget}
                  </div>
                </div>
                
                <div className="chain-steps">
                  {redirectChain.seoAnalysis && (
                    <div className="seo-analysis-section">
                      <SEOScore 
                        score={redirectChain.seoAnalysis.chainScore}
                        grade={redirectChain.seoAnalysis.grade}
                        issues={redirectChain.seoAnalysis.stepAnalyses[0]?.issues || []}
                        recommendations={redirectChain.seoAnalysis.recommendations}
                        showDetails={false}
                      />
                      {redirectChain.seoAnalysis.lengthPenalty > 0 && (
                        <div className="length-penalty">
                          <small>⚠️ Chain length penalty: -{redirectChain.seoAnalysis.lengthPenalty} points</small>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {redirectChain.chain.map((step, index) => (
                    <div key={index} className="redirect-step">
                      <div className="step-number">{index + 1}</div>
                      <div className="step-content">
                        <div className="step-source">
                          <strong>From:</strong> 
                          <code className={redirectChain.hasCycle && step.source === redirectChain.cycleStart ? 'cycle-point' : ''}>
                            {step.source}
                          </code>
                        </div>
                        <div className="step-arrow">→</div>
                        <div className="step-target">
                          <strong>To:</strong> 
                          <code>{step.target}</code>
                        </div>
                        <div className="step-meta">
                          {redirectChain.seoAnalysis?.stepAnalyses[index] && (
                            <SEOScore 
                              score={redirectChain.seoAnalysis.stepAnalyses[index].score}
                              grade={redirectChain.seoAnalysis.stepAnalyses[index].grade}
                              compact={true}
                            />
                          )}
                          <span 
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(step.status) }}
                          >
                            {step.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {redirectChain.hasCycle && (
                    <div className="cycle-warning">
                      ⚠️ This chain contains a cycle starting at: <code>{redirectChain.cycleStart}</code>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NestedRedirects; 